/**
 * @module qbLinkage/detectChanges/mergeChangeSets
 * @description 合并各子检测器 partial 结果为完整 IQBChangeSet
 */

import type {
  IQBChangeSet,
  IQBSnapshot,
  TQBCategory,
  TQBEvent,
} from '../types';
import { snapshotList } from '../fieldSnapshot';
import type { IPartialQBChangeSet } from './partialChangeSet';

function resolveHasDateDimension(snapshot: IQBSnapshot): boolean {
  if (snapshot.hasDateDimension !== undefined) {
    return snapshot.hasDateDimension;
  }
  const dims = snapshotList(snapshot.columnDimensions, 'dimension');
  return dims.some(d => d.column_type === 'date');
}

function mergePartialArrays<T>(
  partials: IPartialQBChangeSet[],
  key: keyof IPartialQBChangeSet,
): T[] {
  const out: T[] = [];
  for (const partial of partials) {
    const value = partial[key];
    if (Array.isArray(value) && value.length) {
      out.push(...(value as T[]));
    }
  }
  return out;
}

export function mergeQBChangeSets(
  categories: Set<TQBCategory>,
  next: IQBSnapshot,
  ...partials: IPartialQBChangeSet[]
): IQBChangeSet {
  const events = new Set<TQBEvent>();
  const merged: Partial<IQBChangeSet> = {};

  for (const partial of partials) {
    if (partial.events) {
      for (const event of partial.events) {
        events.add(event);
      }
    }
    const { events: _events, ...rest } = partial;
    Object.assign(merged, rest);
  }

  const renamed = mergePartialArrays<IQBChangeSet['renamed'][number]>(
    partials,
    'renamed',
  );
  const nextDims = snapshotList(next.columnDimensions, 'dimension');
  const nextMets = snapshotList(next.columnMetrics, 'metric');

  return {
    events,
    categories,
    addedDimensions: mergePartialArrays(partials, 'addedDimensions'),
    removedDimensions: mergePartialArrays(partials, 'removedDimensions'),
    addedMetrics: mergePartialArrays(partials, 'addedMetrics'),
    removedMetrics: mergePartialArrays(partials, 'removedMetrics'),
    addedRawColumns: mergePartialArrays(partials, 'addedRawColumns'),
    removedRawColumns: mergePartialArrays(partials, 'removedRawColumns'),
    reorderedDimensions: merged.reorderedDimensions,
    reorderedMetrics: merged.reorderedMetrics,
    reorderedRawColumns: merged.reorderedRawColumns,
    renamed,
    dimCount: merged.dimCount ?? nextDims.length,
    metricCount: merged.metricCount ?? nextMets.length,
    hasDateDimension: merged.hasDateDimension ?? resolveHasDateDimension(next),
    compareTime: merged.compareTime,
    userFilterGroup: merged.userFilterGroup,
  };
}
